import React, { useState, useEffect, useRef } from 'react'
import './CarInspectionInterface.css'
import Modal from '@mui/material/Modal'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import { useParams } from 'react-router-dom'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'
import CloseIcon from '@mui/icons-material/Close'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import VisibilityIcon from '@mui/icons-material/Visibility'
import CompareIcon from '@mui/icons-material/Compare'
import axios from 'axios'

// Optimized location function with caching
const locationCache = new Map();
const pendingLocationRequests = new Map();

// Background fetch function for locations
const fetchLocationInBackground = async (lat, lng, cacheKey, notify = false) => {
  if (pendingLocationRequests.has(cacheKey)) return;
  pendingLocationRequests.set(cacheKey, true);

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    const latNum = parseFloat(lat);
    const lngNum = parseFloat(lng);

    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latNum}&lon=${lngNum}&zoom=18&addressdetails=1&accept-language=en-IN&countrycodes=in&namedetails=1`,
      { signal: controller.signal,
        headers: {
          'Accept-Language': 'en-IN,en;q=0.9',
          'User-Agent': 'InsuranceApp/1.0' // Required header
        }}
    );

    clearTimeout(timeoutId);

       if (response.ok) {
      const data = await response.json();
      console.log('Nominatim Response:', data); // Debug log
      
      if (data?.display_name) {
        // ✅ Prioritize Indian address format
        let address = data.display_name;
        
        // Extract important parts for Indian addresses
        const addressParts = address.split(',');
        
        // If address seems to be in Japan or other country
        if (address.toLowerCase().includes('japan') || 
            address.toLowerCase().includes('jp') ||
            (data.address && data.address.country && 
             data.address.country.toLowerCase() !== 'india')) {
          
          // Force return coordinates instead of wrong country
          console.warn('Non-Indian address detected, returning coordinates');
          const coordString = `${latNum.toFixed(4)}, ${lngNum.toFixed(4)}`;
          locationCache.set(cacheKey, coordString);
          return coordString;
        }
        
        // Take first 3 parts (usually gives locality, city, state)
        const indianAddress = addressParts.slice(0, 3).join(', ');
        locationCache.set(cacheKey, indianAddress);
        
        if (notify) {
          dispatchLocationUpdate(cacheKey, indianAddress);
        }
        return indianAddress;
      }
    }
  } catch (error) {
    console.warn('Location fetch failed:', error);
  } finally {
    pendingLocationRequests.delete(cacheKey);
  }
  
  // Fallback to coordinates
  const coordString = `${parseFloat(lat).toFixed(4)}, ${parseFloat(lng).toFixed(4)}`;
  locationCache.set(cacheKey, coordString);
  return coordString;
};

// Optimized location function with immediate response
const getLocationFromCoords = (lat, lng) => {
  if (!lat || !lng || lat === 'null' || lng === 'null')
    return 'Loading location...';

  const latNum = parseFloat(lat);
  const lngNum = parseFloat(lng);
  
  if (isNaN(latNum) || isNaN(lngNum)) return 'Invalid coordinates';

  const cacheKey = `${latNum.toFixed(6)},${lngNum.toFixed(6)}`;
  
  // Return cached result immediately
  if (locationCache.has(cacheKey)) {
    return locationCache.get(cacheKey);
  }

  // Show coordinates immediately
  const immediateResult = `${latNum.toFixed(4)}, ${lngNum.toFixed(4)}`;
  
  // Start background fetch (no await - fire and forget)
  fetchLocationInBackground(latNum, lngNum, cacheKey, true);
  
  return immediateResult;
};

// Event system for location updates
const dispatchLocationUpdate = (cacheKey, address) => {
  const event = new CustomEvent('locationUpdated', {
    detail: { cacheKey, address }
  });
  window.dispatchEvent(event);
};

// Add preload function for locations
const preloadLocation = (lat, lng) => {
  if (!lat || !lng) return;
  const latNum = parseFloat(lat);
  const lngNum = parseFloat(lng);
  
  if (!isNaN(latNum) && !isNaN(lngNum)) {
    const cacheKey = `${latNum.toFixed(6)},${lngNum.toFixed(6)}`;
    if (!locationCache.has(cacheKey) && !pendingLocationRequests.has(cacheKey)) {
      fetchLocationInBackground(latNum, lngNum, cacheKey);
    }
  }
};


// Date function with timezone handling
const formatDateTime = timestamp => {
  if (!timestamp || timestamp === 'null') return 'Loading data...'

  try {
    const date = new Date(timestamp)
    if (isNaN(date.getTime())) return 'Invalid date'

    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
  } catch (error) {
    return 'Invalid date format'
  }
}

// Constants for styling and configuration
const STYLES = {
  container: {
    position: 'fixed',
    top: 55,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '95%',
    maxWidth: '100%',
    maxHeight: '100%',
    background: 'white',
    borderRadius: '0',
    boxShadow: 'none',
    overflowY: 'auto',
    overflowX: 'hidden',
    zIndex: 100,
    transform: 'none'
  },
  header: {
    backgroundColor: '#f9fafb',
    padding: '1.5rem',
    boxShadow: '0 2px 4px 10px rgba(255, 255, 255, 0.1)',
    borderBottom: '2px solid rgb(193,193,193)'
  },
  headerFlex: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1rem 3rem',
    flex: 1
  },
  threeColumnGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1rem 2rem',
    flex: 1
  },
  label: {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#000000ff',
    marginBottom: '0.25rem'
  },
  value: {
    fontSize: '0.875rem',
    color: '#111827'
  },
  doneButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '1rem 0.5rem 0',
    width: '100%'
  },
  doneButton: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginRight: '15px',
    padding: '0.5rem 1rem',
    backgroundColor: 'white',
    color: 'rgb(236 67 153)',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    border: '1px solid rgb(236 67 153)',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
    '&:hover': {
      backgroundColor: 'rgb(236 67 153)',
      color: 'white'
    }
  },
  legend: {
    padding: '1.5rem',
    borderBottom: '1px solid #e5e7eb',
    backgroundColor: '#f9fafb'
  },
  legendFlex: {
    display: 'flex',
    alignItems: 'center',
    gap: '6rem'
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  },
  dot: {
    width: '0.75rem',
    height: '0.75rem',
    borderRadius: '50%'
  },
  legendText: {
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#374151'
  },
  imagesSection: {
    padding: '1.5rem'
  },
  imagesGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1.5rem'
  },
  singleImageGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1.5rem',
    justifyContent: 'center'
  },
  imageTitle: {
    fontSize: '1.125rem',
    fontWeight: '500',
    color: '#111827',
    marginBottom: '1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  },
  imageContainer: {
    position: 'relative',
    backgroundColor: '#f3f4f6',
    borderRadius: '0.5rem',
    overflow: 'hidden',
    height: '565px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  singleImageContainer: {
    position: 'relative',
    backgroundColor: '#f3f4f6',
    borderRadius: '0.5rem',
    overflow: 'hidden',
    height: '415px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: '600px',
    margin: '0 auto'
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'contain'
  },
  expandButton: {
    position: 'absolute',
    top: '1rem',
    right: '1rem',
    width: '2rem',
    height: '2rem',
    backgroundColor: '#ef4444',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    cursor: 'pointer'
  },
  navigation: {
    padding: '0 1.5rem 1.5rem'
  },
  navigationFlex: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  prevButton: {
    padding: '0.5rem 1.5rem',
    border: '1px solid #ec4899',
    color: '#ec4899',
    borderRadius: '0.375rem',
    fontWeight: '500',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    '&:hover:not(:disabled)': {
      backgroundColor: '#ec4899',
      color: 'white'
    }
  },
  nextButton: {
    padding: '0.5rem 1.5rem',
    backgroundColor: '#ec4899',
    color: 'white',
    borderRadius: '0.375rem',
    fontWeight: '500',
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    '&:hover:not(:disabled)': {
      backgroundColor: '#d13687'
    }
  },
  disabledButton: {
    opacity: 0.5,
    cursor: 'not-allowed'
  },
  Gridcss: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  boundingbox: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  modal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 1400,
    height: 700,
    bgcolor: 'background.paper',
    boxShadow: 20,
    p: 0,
    borderRadius: '0.5rem',
    border: 'none',
    outline: 'none',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden' 
  },
  modalHeader: {
    padding: '1rem',
    borderBottom: '1px solid #e5e7eb',
    backgroundColor: '#f9fafb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexShrink: 0
  },
  modalBody: {
    padding: '1rem',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f3f4f6',
    flexGrow: 1,
    overflow: 'auto',
    position: 'relative'
  },
  modalImageContainer: {
    position: 'relative',
    display: 'inline-block',
    transition: 'transform 0.3s ease',
    transformOrigin: 'center center'
  },
  modalImage: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    display: 'block'
  },
  modalFooter: {
    padding: '1rem',
    borderTop: '1px solid #e5e7eb',
    backgroundColor: '#f9fafb',
    textAlign: 'center',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#374151',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexShrink: 0
  },
  zoomControl: {
    display: 'flex',
    alignItems: 'center',
    border: '1px solid #FF0083',
    borderRadius: '0.375rem'
  },
  zoomButton: {
    padding: '0.5rem',
    backgroundColor: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#FF0083',
    '&:hover': {
      backgroundColor: '#fce7f3'
    }
  },
  zoomPercentage: {
    fontSize: '1rem',
    fontWeight: 'bold',
    minWidth: '50px',
    color: '#FF0083',
    textAlign: 'center'
  },
  AIprocessed: {
    padding: '0.5rem 1rem',
    border: '1px solid #FF0083',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    backgroundColor: '#FF0083',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#d13687'
    }
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    gap: '1rem',
    color: '#6b7280'
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    color: '#6b7280',
    textAlign: 'center',
    gap: '1rem'
  },
  errorIcon: {
    fontSize: '3rem',
    color: '#9ca3af'
  },
  imageStatus: {
    position: 'absolute',
    top: '0rem',
    left: '1rem',
    padding: '0.25rem 0.5rem',
    borderRadius: '0.25rem',
    fontSize: '0.75rem',
    fontWeight: '500'
  },
  availableStatus: {
    backgroundColor: 'rgba(16, 185, 129, 0.1)',
    color: '#065f46'
  },
  unavailableStatus: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    color: '#991b1b'
  }
}

const STATIC_DAMAGE_TYPES = [
  { color: '#CE036B', label: 'Dent', classId: 'bb79' },
  { color: '#FF9800', label: 'Scratch', classId: '8abc' },
  { color: '#AC43FC', label: 'Broken', classId: '09q2' },
  { color: '#2196F3', label: 'Windshield(Damage)', classId: 'windshield' }
];

//  UPDATED: Function to get complete damage types
const getDynamicDamageTypes = (isSwap) => {
  const damageTypes = [...STATIC_DAMAGE_TYPES];
  
  if (isSwap) {
    damageTypes.push({ color: '#ff0000ff', label: 'Swap', classId: 'Swap' });
  } else {
    damageTypes.push({ color: '#09ff00ff', label: 'No-Swap', classId: 'No-Swap' });
  }
  
  return damageTypes;
};

const classColorMap = {
  bb79: '#CE036B',
  '8abc': '#FF9800',
  windshield: '#2196F3',
  '09q2': '#AC43FC',
   'Swap': '#ff0000ff',
  'No-Swap': '#09ff00ff'
}

const fallbackImage =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxyZWN0IHg9IjUwIiB5PSIyMDAiIHdpZHRoPSIzMDAiIGhlaWdodD0iODAiIHJ4PSI0IiBmaWxsPSIjOUNBM0FGIi8+CjxyZWN0IHg9IjEwMCIgeT0iMTIwIiB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEwMCIgcng9IjgiIGZpbGw9IiM2Mzc0OEYiLz4KPHJlY3QgeD0iNzAiIHk9IjE0MCIgd2lkdGg9IjI2MCIgaGVpZ2h0PSI0MCIgcng9IjIwIiBmaWxsPSIjMUYyOTM3Ii8+CjxyZWN0IHg9IjEyMCIgeT0iMjMwIiB3aWR0aD0iMTYwIiBoZWlnaHQ9IjIwIiByeD0iNCIgZmlsbD0iIzM3NDE1MSIvPgo8dGV4dCB4PSIyMDAiIHk9IjI0NSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0id2hpdGUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMiI+REwyQ0JGMDU2MDwvdGV4dD4KPHN2ZyB4PSIzNjAiIHk9IjIwIiB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSI+CjxwYXRoIGQ9Ik0xMiAxOEw2IDEyTDEyIDZMMTggMTJMMTIgMThaIiBmaWxsPSIjRUY0NDQ0Ii8+CjwvdGV4dD4KPC9zdmc+'

// Image preloading and caching system
class ImageCacheManager {
  constructor () {
    this.cache = new Map()
    this.preloaded = new Set()
    this.loadingPromises = new Map()
  }

  async preloadImage (index, getImageUrl) {
    if (this.preloaded.has(index)) {
      return this.cache.get(index)
    }

    // If already loading, return the existing promise
    if (this.loadingPromises.has(index)) {
      return this.loadingPromises.get(index)
    }

    const loadPromise = (async () => {
      try {
        const imageUrl = await getImageUrl()
        if (imageUrl) {
          // Preload the actual image
          await this.loadImageToMemory(imageUrl)
          this.cache.set(index, imageUrl)
          this.preloaded.add(index)
        }
        return imageUrl
      } catch (error) {
        console.error(`Error preloading image ${index}:`, error)
        return null
      } finally {
        this.loadingPromises.delete(index)
      }
    })()

    this.loadingPromises.set(index, loadPromise)
    return loadPromise
  }

  async loadImageToMemory (src) {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(src)
      img.onerror = reject
      img.src = src
    })
  }

  getImage (index) {
    return this.cache.get(index)
  }

  hasImage (index) {
    return this.preloaded.has(index)
  }

  isPreloading (index) {
    return this.loadingPromises.has(index)
  }

  clear () {
    this.cache.clear()
    this.preloaded.clear()
    this.loadingPromises.clear()
  }
}

const imageCacheManager = new ImageCacheManager()

const shouldShowAIProcessed = currentSection => {
  if (!currentSection) return true

  const sectionLower = currentSection.toLowerCase()
  const excludeSections = ['odometer', 'chassis', 'rc', 'engine']
  
  // Check if current section contains any excluded keyword
  const isExcluded = excludeSections.some(exclude => 
    sectionLower.includes(exclude.toLowerCase())
  )
  
  return !isExcluded
}

const isFrontOrBackSection = currentSection => {
  if (!currentSection) return false

  const sectionLower = currentSection.toLowerCase()
  const excludedSections = [
    'front-left',
    'front_left',
    'front-right',
    'front_right',
    'back-left',
    'back_left',
    'back-right',
    'back_right',
    'left',
    'right'
  ]

  if (excludedSections.some(excluded => sectionLower.includes(excluded))) {
    return false
  }

  return sectionLower === 'front' || sectionLower === 'back'
}

export default function CarInspectionInterface ({
  currentIndex,
  sections,
  registrationNumber,
  onClose,
  onNext,
  onPrev,
  getImageURL,
  uploadedImages,
  existingImages
}) {
  const [open, setOpen] = useState(false)
  const [zoomLevel, setZoomLevel] = useState(100)
  const [clickedImageType, setClickedImageType] = useState('')
  const [showAIProcessed, setShowAIProcessed] = useState(true)
  const [modalShowAIProcessed, setModalShowAIProcessed] = useState(true)

  const [updateInfoData, setUpdateInfoData] = useState(null)
  
  const [manualUpdates, setManualUpdates] = useState({
    odometer_manual_update: null,
    chassis_manual_update: null,
    rc_manual_update: null,
    engine_manual_update: null
  })

   //  NEW STATE FOR CLAIM DETAILS
  const [claimDetails, setClaimDetails] = useState(null)
  const [isSwap, setIsSwap] = useState(false)

  // Optimized states
  const [currentImageUrl, setCurrentImageUrl] = useState(null)
  const [loadingStates, setLoadingStates] = useState({})
  const [imageErrors, setImageErrors] = useState({})

  const [auditDataMap, setAuditDataMap] = useState({})
  const [imgSizes, setImgSizes] = useState({})
  const [isClaimProcessed, setIsClaimProcessed] = useState(false)

  const { claimNumber } = useParams()
  const isMountedRef = useRef(true)
  const [, setLocationUpdateTick] = useState(0)

  useEffect(() => {
    const handleLocationUpdate = () => {
      setLocationUpdateTick(prev => prev + 1)
    }
    window.addEventListener('locationUpdated', handleLocationUpdate)
    return () => {
      window.removeEventListener('locationUpdated', handleLocationUpdate)
    }
  }, [])

  // Enhanced helper functions
  const findEntryForSection = (sectionName) => {
    if (!sectionName || !auditDataMap) return null;
    
    const sectionLower = sectionName.toLowerCase();
    
    // First try exact match from sections
    for (const [key, entry] of Object.entries(auditDataMap)) {
      if (!entry?.image_name) continue;
      
      const entryName = entry.image_name.toLowerCase();
      
      if (sectionLower.includes('additional')) {
        const sectionNum = sectionName.match(/\d+/)?.[0];
        if (sectionNum) {
          if (entryName.includes(`additional_image_${sectionNum}`) ||
              entryName.includes(`additional_${sectionNum}`) || 
              entryName.includes(`additional${sectionNum}`)) {
            return entry;
          }
        }
      }
      
      // Match standard sections
      if (sectionLower === 'front-left' || sectionLower === 'front_left') {
        if (entryName.includes('front_left')) return entry;
      } else if (sectionLower === 'front-right' || sectionLower === 'front_right') {
        if (entryName.includes('front_right')) return entry;
      } else if (sectionLower === 'back-left' || sectionLower === 'back_left') {
        if (entryName.includes('back_left')) return entry;
      } else if (sectionLower === 'back-right' || sectionLower === 'back_right') {
        if (entryName.includes('back_right')) return entry;
      } else if (sectionLower === 'back') {
        if (entryName.includes('back') && 
            !entryName.includes('back_left') && 
            !entryName.includes('back_right')) return entry;
      } else if (sectionLower === 'left') {
        if (entryName.includes('left') && 
            !entryName.includes('front_left') && 
            !entryName.includes('back_left')) return entry;
      } else if (sectionLower === 'right') {
        if (entryName.includes('right') && 
            !entryName.includes('front_right') && 
            !entryName.includes('back_right')) return entry;
      } else if (sectionLower === 'front') {
        if (entryName.includes('front') && 
            !entryName.includes('front_left') && 
            !entryName.includes('front_right')) return entry;
      } else if (sectionLower.includes('windshield') && sectionLower.includes('outer')) {
        if (entryName.includes('windshield') && entryName.includes('outer')) return entry;
      } else if (sectionLower.includes('windshield') && sectionLower.includes('inner')) {
        if (entryName.includes('windshield') && entryName.includes('inner')) return entry;
      } else if (sectionLower.includes('tyre')) {
        if (entryName.includes('tyre')) return entry;
      } else if (sectionLower.includes('odometer')) {
        if (entryName.includes('odometer')) return entry;
      } else if (sectionLower.includes('chassis')) {
        if (entryName.includes('chassis')) return entry;
      } else if (sectionLower.includes('rc') || sectionLower.includes('registration')) {
        if (entryName.includes('rc') || entryName.includes('registration')) return entry;
      }
      else if (sectionLower.includes('engine')) {
      if (entryName.includes('engine')) return entry;
    }
    }
    
    // Fallback: try to find by section name
    for (const [key, entry] of Object.entries(auditDataMap)) {
      if (!entry?.image_name) continue;
      const entryName = entry.image_name.toLowerCase();
      if (entryName.includes(sectionLower.replace(/[-\s]/g, '_'))) {
        return entry;
      }
    }
    
    return null;
  };

const getFieldValue = (fieldName) => {
  const currentEntry = findEntryForSection(currentSection);
  
  if (!currentEntry) return 'Not Found';
  
  // Special handling for different fields
  if (fieldName === 'odometer_reading') {
    // Priority 1: Manual updated value
    if (manualUpdates?.odometer_manual_update) {
      return manualUpdates.odometer_manual_update;
    }
    // Priority 2: Original audit data
    return currentEntry?.OdoMeterReading || 
           currentEntry?.odometer || 
           'Not Found';
  } 
  else if (fieldName === 'chassis_number') {
    // Priority 1: Manual updated value
    if (manualUpdates?.chassis_manual_update) {
      return manualUpdates.chassis_manual_update;
    }
    // Priority 2: Original audit data
    return currentEntry?.ChassisNumber || 
           currentEntry?.chassis ||  
           'Not Found';
  }
  else if (fieldName === 'rc_number') {
    // Priority 1: Manual updated value
    if (manualUpdates?.rc_manual_update) {
      return manualUpdates.rc_manual_update;
    }
    // Priority 2: Original audit data
    return currentEntry?.EngineNumber || 
           currentEntry?.rc || 
           'Not Found';
  }
  else if (fieldName === 'engine_number') {
    // Priority 1: Manual updated value
    if (manualUpdates?.engine_manual_update) {
      return manualUpdates.engine_manual_update;
    }
    // Priority 2: Original audit data
    return currentEntry?.EngineNumber || 
           currentEntry?.engine || 
           'Not Found';
  }
  else if (fieldName === 'date') {
    return currentEntry?.clicked_time || 
           currentEntry?.timestamp || 
           'Not Found';
  }
  
  return currentEntry?.[fieldName] || 'Not Found';
};


const getCurrentLocation = () => {
  const currentEntry = findEntryForSection(currentSection);
  
  if (!currentEntry?.latitude || !currentEntry?.longitude) {
    return 'Location not available';
  }

  const lat = currentEntry.latitude;
  const lng = currentEntry.longitude;
  
  if (!lat || !lng || lat === 'null' || lng === 'null' || lat === '0' || lng === '0')
    return 'Loading location...';

  const latNum = parseFloat(lat);
  const lngNum = parseFloat(lng);
  
  if (isNaN(latNum) || isNaN(lngNum) || (latNum === 0 && lngNum === 0)) return 'Location not available';

  const cacheKey = `${latNum.toFixed(6)},${lngNum.toFixed(6)}`;

  if (locationCache.has(cacheKey)) {
    return locationCache.get(cacheKey);
  }

  const immediateResult = `${latNum.toFixed(4)}, ${lngNum.toFixed(4)}`;
  fetchLocationInBackground(latNum, lngNum, cacheKey, true);
  return immediateResult;
};


// const getCurrentLocation = () => {
//   const currentEntry = findEntryForSection(currentSection);
  
//   if (!currentEntry?.latitude || !currentEntry?.longitude) {
//     return 'Location not available';
//   }

//   const lat = currentEntry.latitude;
//   const lng = currentEntry.longitude;
  
//   if (!lat || !lng || lat === 'null' || lng === 'null')
//     return 'Loading location...';

//   const latNum = parseFloat(lat);
//   const lngNum = parseFloat(lng);
  
//   if (isNaN(latNum) || isNaN(lngNum)) return 'Invalid coordinates';

//   const cacheKey = `${latNum.toFixed(6)},${lngNum.toFixed(6)}`;

//   // ✅ SPECIAL HANDLING FOR ADDITIONAL IMAGES
//   const isAdditionalImage = currentSection?.toLowerCase().includes('additional');

//   // here bydefault value i have taken as static
//   if (isAdditionalImage) {
//     return "not found";
//   }

//    if (locationCache.has(cacheKey)) {
//     return locationCache.get(cacheKey);
//   }
  
//   if (isAdditionalImage) {
//     // Special cache key for additional images
//     const additionalCacheKey = `additional_${cacheKey}`;
    
//     if (locationCache.has(additionalCacheKey)) {
//       return locationCache.get(additionalCacheKey);
//     }
    
//     // Show coordinates immediately for additional images
//     const immediateResult = `${latNum.toFixed(4)}, ${lngNum.toFixed(4)}`;
    
//     // Start background fetch WITH ENGLISH LOCALE
//     fetchLocationForAdditionalImage(latNum, lngNum, additionalCacheKey, true);
    
//     return immediateResult;
//   }
  
//   // ✅ Original logic for OTHER images (working fine)
//   if (locationCache.has(cacheKey)) {
//     return locationCache.get(cacheKey);
//   }

//   const immediateResult = `${latNum.toFixed(4)}, ${lngNum.toFixed(4)}`;
//   fetchLocationInBackground(latNum, lngNum, cacheKey, true);
//   return immediateResult;
// };

const fetchLocationForAdditionalImage = async (lat, lng, cacheKey, notify = false) => {
  return fetchLocationInBackground(lat, lng, cacheKey, notify);
};

//  yha se discrepancies_exchange values fetch krna h
useEffect(() => {
  const fetchDiscrepancies = async () => {
    if (!claimNumber) return;
    
    try {
      console.log('Fetching discrepancies for:', claimNumber);
      
      const response = await axios.get('/process_data/discrepancies_exchange', {
        params: {
          claim_number: claimNumber
        }
      });
      
      console.log('API Response:', response.data);
      
      if (response.data.success) {
        const exchangeValue = response.data.claimDetails.discrepancies_exchange;
        
        // Check if value is "swap" (case-insensitive)
        const isSwapValue = exchangeValue && 
                           exchangeValue.toString().toLowerCase().trim() === 'swap';
        
        console.log('Exchange value:', exchangeValue, 'Is Swap:', isSwapValue);
        
        setIsSwap(isSwapValue);
        setClaimDetails(response.data.claimDetails);
      }
      
    } catch (error) {
      console.error('Error fetching discrepancies:', error);
      setIsSwap(false); // Default to No-Swap
    }
  };
  
  fetchDiscrepancies();
}, [claimNumber]);

  useEffect(() => {
    const fetchUpdateInfoData = async () => {
      if (!claimNumber) return;
      
      try {
        const response = await axios.post(
          `/server/get-claim-data/${claimNumber}`
        );
        setUpdateInfoData(response.data);
        console.log('CarInspection - UpdateInfo Data:', response.data);
      } catch (error) {
        console.error('Error fetching UpdateInfo data:', error);
      }
    };
    
    fetchUpdateInfoData();
  }, [claimNumber]);

  useEffect(() => {
    const fetchManualUpdates = async () => {
      if (!claimNumber) return;
      
      try {
        const response = await axios.post(
          `/process_data/get-manual-updates/${claimNumber}`
        );
        
        if (response.data.success) {
          setManualUpdates({
            odometer_manual_update: response.data.odometer_manual_update,
            chassis_manual_update: response.data.chassis_manual_update,
            rc_manual_update: response.data.rc_manual_update,
            engine_manual_update: response.data.engine_manual_update || null
          });
          console.log('Manual Updates:', response.data);
        } else {
          // No manual updates found
          setManualUpdates({
            odometer_manual_update: null,
            chassis_manual_update: null,
            rc_manual_update: null,
            engine_manual_update: null
          });
        }
      } catch (error) {
        console.error('Error fetching manual updates:', error);
        setManualUpdates({
          odometer_manual_update: null,
          chassis_manual_update: null,
          rc_manual_update: null,
          engine_manual_update: null
        });
      }
    };
    
    fetchManualUpdates();
  }, [claimNumber]);

  // Cache cleanup effect
  useEffect(() => {
    imageCacheManager.clear()
    console.log('Image cache cleared for new claim')

    return () => {
      imageCacheManager.clear()
      isMountedRef.current = false
      console.log('Image cache cleared on component unmount')
    }
  }, [claimNumber])

  // Fetch audit data
  useEffect(() => {
    const fetchRes = async () => {
      try {
        const res = await axios.post('/process_data/get_audit_info', {
          audit_id: claimNumber
        })
        const payload = Array.isArray(res.data) ? res.data : [res.data]

        const map = {}
        payload.forEach(item => {
          const key = item.image_name || item.imageName || item.imageURL || ''
          if (key) map[key] = item
        })

        setAuditDataMap(map)
        console.log('Audit data loaded:', map);
      } catch (error) {
        console.error('Fetch mode error:', error)
      }
    }

    if (claimNumber) fetchRes()
  }, [claimNumber])

  // Fetch annotation status
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await axios.post('/process_data/check_annotation_status', {
          claim_id: claimNumber
        })
        setIsClaimProcessed(res.data?.is_processed === 'processed')
      } catch (err) {
        console.error('Error fetching annotation status:', err)
      }
    }

    if (claimNumber) fetchStatus()
  }, [claimNumber])

  // Image loading logic
  useEffect(() => {
    isMountedRef.current = true

    const initializeImages = async () => {
      const cachedImage = imageCacheManager.getImage(currentIndex)
      if (cachedImage && isMountedRef.current) {
        setCurrentImageUrl(cachedImage)
        setLoadingStates(prev => ({ ...prev, [currentIndex]: false }))
      } else {
        setLoadingStates(prev => ({ ...prev, [currentIndex]: true }))
        loadCurrentImage()
      }

      preloadAdjacentImages(currentIndex)
    }

    initializeImages()

    return () => {
      isMountedRef.current = false
    }
  }, [currentIndex, sections])

  const getImageURLForIndex = async index => {
    if (imageCacheManager.hasImage(index)) {
      return imageCacheManager.getImage(index)
    }

    if (imageCacheManager.isPreloading(index)) {
      return imageCacheManager.loadingPromises.get(index)
    }

    const section = sections[index]
    const existingUrl = existingImages?.[section]

    if (existingUrl) {
      imageCacheManager
        .preloadImage(index, () => Promise.resolve(existingUrl))
        .catch(err => console.warn(`Preload failed for ${section}`, err))
      return existingUrl
    }

    const apiUrl = await getImageURL(section, index)
    if (apiUrl) {
      await imageCacheManager.preloadImage(index, () => Promise.resolve(apiUrl))
    }

    return apiUrl
  }

  const loadCurrentImage = async () => {
    if (!isMountedRef.current) return

    try {
      const imageUrl = await getImageURLForIndex(currentIndex)
      if (imageUrl && isMountedRef.current) {
        setCurrentImageUrl(imageUrl)
        setLoadingStates(prev => ({ ...prev, [currentIndex]: false }))
      }
    } catch (error) {
      console.error(`Error loading current image:`, error)
      if (isMountedRef.current) {
        setImageErrors(prev => ({ ...prev, [currentIndex]: true }))
        setLoadingStates(prev => ({ ...prev, [currentIndex]: false }))
      }
    }
  }

  const preloadAdjacentImages = currentIdx => {
    for (let i = 1; i <= 3; i++) {
      const nextIndex = currentIdx + i
      if (nextIndex < sections.length && !imageCacheManager.hasImage(nextIndex)) {
        preloadImageByIndex(nextIndex)
      }
      const prevIndex = currentIdx - i
      if (prevIndex >= 0 && !imageCacheManager.hasImage(prevIndex)) {
        preloadImageByIndex(prevIndex)
      }
    }
  }

  const preloadImageByIndex = async index => {
    if (imageCacheManager.hasImage(index) || imageCacheManager.isPreloading(index)) {
      return
    }

    try {
      await getImageURLForIndex(index)
    } catch (error) {
      console.error(`Error preloading image ${index}:`, error)
    }
  }

  // ImageWithBoxes component
  const ImageWithBoxes = ({
    src,
    imageName,
    currentIndexForError,
    showBoxes = true,
    zoomLevel = 100
  }) => {
    const currentEntry = findEntryForSection(imageName);
    const boxes = showBoxes
      ? currentEntry?.result?.filter(b => b?.width > 0 && b?.height > 0) ?? []
      : []

    const sizes = imgSizes[src] || {}
    const naturalW = sizes.naturalW || 0
    const naturalH = sizes.naturalH || 0
    const renderedW = sizes.renderedW || naturalW || 0
    const renderedH = sizes.renderedH || naturalH || 0

    const getColorForClass = classId => {
      const color = classColorMap[classId]
      return color || '#FF0000'
    }

    const getClassIdFromBox = box => {
      return (
        box.class_id ||
        box.classId ||
        box.class ||
        box.label ||
        box.damage_type ||
        box.id ||
        ''
      )
    }

    const handleImgLoad = e => {
      const el = e.target
      setImgSizes(prev => ({
        ...prev,
        [src]: {
          naturalW: el.naturalWidth,
          naturalH: el.naturalHeight,
          renderedW: el.clientWidth,
          renderedH: el.clientHeight
        }
      }))
    }

    return (
      <div
        style={{
          ...STYLES.modalImageContainer,
          transform: `scale(${zoomLevel / 100})`
        }}
      >
        <img
          src={src}
          alt={imageName || 'audit-image'}
          loading='eager'
          decoding='sync'
          style={{ ...STYLES.modalImage, objectFit: 'contain' }}
          onLoad={handleImgLoad}
          onError={e => {
            e.target.src = fallbackImage
            if (typeof setImageErrors === 'function' && typeof currentIndexForError !== 'undefined') {
              setImageErrors(prev => ({
                ...(prev || {}),
                [currentIndexForError]: true
              }))
            }
          }}
        />

        {boxes.map((box, i) => {
          if (!box || box.type !== 'RECTANGLE') return null

          let top = box.y ?? 0
          let left = box.x ?? 0
          let width = box.width ?? 0
          let height = box.height ?? 0

          if (naturalW > 0 && naturalH > 0 && renderedW > 0 && renderedH > 0) {
            top = (box.y / naturalH) * renderedH
            left = (box.x / naturalW) * renderedW
            width = (box.width / naturalW) * renderedW
            height = (box.height / naturalH) * renderedH
          }

          const classId = getClassIdFromBox(box)
          const boxColor = getColorForClass(classId)
          // ❌ Skip drawing if color is red
          if (boxColor === '#FF0000') return null
          const damageType = getDynamicDamageTypes(isSwap).find(type => type.classId === classId)?.label || classId || 'Unknown'

          return (
            <div
              key={`${box.id || i}`}
              title={`${damageType} (${classId})`}
              style={{
                position: 'absolute',
                top: `${top}px`,
                left: `${left}px`,
                width: `${width}px`,
                height: `${height}px`,
                border: `2px solid ${boxColor}`,
                boxSizing: 'border-box',
                pointerEvents: 'none',
                background: 'none',
                borderRadius: '2px'
              }}
            />
          )
        })}
      </div>
    )
  }

  // Get current section
  const currentSection = sections[currentIndex]

  // Check if current section should show AI processed image
  const showAIProcessedSection = shouldShowAIProcessed(currentSection)

  // Check if current section is front or back side
  const isFrontBackSection = isFrontOrBackSection(currentSection)

  // Handle next button click - instant navigation
  const handleNext = () => {
    if (currentIndex < sections.length - 1 && imageCacheManager.hasImage(currentIndex + 1)) {
      onNext()
    }
  }

  // Handle previous button click - instant navigation
  const handlePrev = () => {
    if (currentIndex > 0 && imageCacheManager.hasImage(currentIndex - 1)) {
      onPrev()
    }
  }

  const handleOpen = imageType => {
    if (!currentImageUrl) return
    setClickedImageType(imageType)
    setModalShowAIProcessed(imageType === 'ai')
    setOpen(true)
    setZoomLevel(100)
  }

  const handleCloseModal = () => setOpen(false)

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 25, 300))
  }

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 25, 50))
  }

  const toggleAIProcessed = () => {
    setShowAIProcessed(!showAIProcessed)
  }

  const toggleModalAIProcessed = () => {
    setModalShowAIProcessed(!modalShowAIProcessed)
  }

  // Check if current image is loading
  const isCurrentImageLoading = loadingStates[currentIndex] === true
  const isCurrentImageAvailable = currentImageUrl !== null

  return (
    <div className='img-modal-container' style={STYLES.container}>
      {/* Header */}
      <div className='btnn-class' style={STYLES.doneButtonContainer}>
        <button onClick={onClose} className='done-btn' style={STYLES.doneButton}>
          Back
        </button>
      </div>

      <div className='grid-boxer-container' style={STYLES.header}>
        <div style={STYLES.headerFlex}>
          {isFrontBackSection ? (
            <div style={STYLES.threeColumnGrid}>
              <div className='grid-boxer' style={STYLES.Gridcss}>
                <div style={STYLES.label}>Photo Type</div>
                <span style={STYLES.value}>{currentSection?.toUpperCase() || 'N/A'}</span>
              </div>
              <div className='grid-boxer' style={STYLES.Gridcss}>
                <div style={STYLES.label}>Date</div>
                <div style={STYLES.value}>
                  {formatDateTime(getFieldValue('date'))}
                </div>
              </div>
              <div className='grid-boxer' style={STYLES.Gridcss}>
                <div style={STYLES.label}>Location</div>
                <div style={STYLES.value}>{getCurrentLocation()}</div>
              </div>
              <div className='grid-boxer' style={STYLES.Gridcss}>
                <div style={STYLES.label}>Claim Number</div>
                <div style={STYLES.value}>{registrationNumber}</div>
              </div>
              {(currentSection?.toLowerCase().includes('engine')) && (
      <div className='grid-boxer' style={STYLES.Gridcss}>
        <div style={STYLES.label}>Engine Number</div>
        <div style={STYLES.value}>
          {getFieldValue('engine_number')}
        </div>
      </div>
    )}
              {currentSection?.toLowerCase().includes('odometer') && (
                <div className='grid-boxer' style={STYLES.Gridcss}>
                  <div style={STYLES.label}>Odometer Reading</div>
                  <div style={STYLES.value}>
                    {getFieldValue('odometer_reading')}
                  </div>
                </div>
              )}
              
              {currentSection?.toLowerCase().includes('chassis') && (
                <div className='grid-boxer' style={STYLES.Gridcss}>
                  <div style={STYLES.label}>Chassis Number</div>
                  <div style={STYLES.value}>
                    {getFieldValue('chassis_number')}
                  </div>
                </div>
              )}
              
              {(currentSection?.toLowerCase().includes('rc') || 
                currentSection?.toLowerCase().includes('registration')) && (
                <div className='grid-boxer' style={STYLES.Gridcss}>
                  <div style={STYLES.label}>RC Number</div>
                  <div style={STYLES.value}>
                    {getFieldValue('rc_number')}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div style={STYLES.grid}>
              <div className='grid-boxer' style={STYLES.Gridcss}>
                <div style={STYLES.label}>Photo Type</div>
                <span style={STYLES.value}>{currentSection?.toUpperCase() || 'N/A'}</span>
              </div>
              <div className='grid-boxer' style={STYLES.Gridcss}>
                <div style={STYLES.label}>Date</div>
                <div style={STYLES.value}>
                  {formatDateTime(getFieldValue('date'))}
                </div>
              </div>
              <div className='grid-boxer' style={STYLES.Gridcss}>
                <div style={STYLES.label}>Location</div>
                <div style={STYLES.value}>{getCurrentLocation()}</div>
              </div>
              <div className='grid-boxer' style={STYLES.Gridcss}>
                <div style={STYLES.label}>Claim Number</div>
                <div style={STYLES.value}>{registrationNumber}</div>
              </div>
              
{(currentSection?.toLowerCase().includes('engine')) && (
      <div className='grid-boxer' style={STYLES.Gridcss}>
        <div style={STYLES.label}>Engine Number</div>
        <div style={STYLES.value}>
          {getFieldValue('engine_number')}
        </div>
      </div>
    )}

              {currentSection?.toLowerCase().includes('odometer') && (
                <div className='grid-boxer' style={STYLES.Gridcss}>
                  <div style={STYLES.label}>Odometer Reading</div>
                  <div style={STYLES.value}>
                    {getFieldValue('odometer_reading')}
                  </div>
                </div>
              )}
              
              {currentSection?.toLowerCase().includes('chassis') && (
                <div className='grid-boxer' style={STYLES.Gridcss}>
                  <div style={STYLES.label}>Chassis Number</div>
                  <div style={STYLES.value}>
                    {getFieldValue('chassis_number')}
                  </div>
                </div>
              )}
              
              {(currentSection?.toLowerCase().includes('rc') || 
                currentSection?.toLowerCase().includes('registration')) && (
                <div className='grid-boxer' style={STYLES.Gridcss}>
                  <div style={STYLES.label}>RC Number</div>
                  <div style={STYLES.value}>
                    {getFieldValue('rc_number')}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Damage Type Legend */}
      {showAIProcessedSection && isClaimProcessed && (
        <div style={STYLES.legend}>
          <div style={STYLES.legendFlex}>
            {getDynamicDamageTypes(isSwap).map((type, index) => (
              <div key={index} style={STYLES.legendItem}>
                <div style={{ ...STYLES.dot, backgroundColor: type.color }}></div>
                <span style={STYLES.legendText}>{type.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Image Comparison */}
      <div style={STYLES.imagesSection}>
        {isClaimProcessed && showAIProcessedSection ? (
          <div style={STYLES.imagesGrid}>
            {/* Original Image */}
            <div>
              <h3 style={STYLES.imageTitle}>
                {showAIProcessed ? (
                  <>
                    <VisibilityIcon /> Original Image
                  </>
                ) : (
                  <>
                    <CompareIcon /> AI Processed Image
                  </>
                )}
              </h3>
              <div className='bounding-box-first' style={STYLES.boundingbox}>
                <img
                  src={`${process.env.PUBLIC_URL}/custom-icons/Boundry-box.png`}
                  style={{
                    cursor: isCurrentImageAvailable ? 'pointer' : 'not-allowed',
                    opacity: isCurrentImageAvailable ? 1 : 0.5
                  }}
                  onClick={() => isCurrentImageAvailable && handleOpen(showAIProcessed ? 'original' : 'ai')}
                  alt='Bounding box'
                  title={isCurrentImageAvailable ? 'Expand image' : 'Image not available'}
                />
              </div>

              <div className='bounding-box-img-first' style={STYLES.imageContainer}>
                {isCurrentImageLoading ? (
                  <div style={STYLES.loadingContainer}>
                    <CircularProgress size={40} />
                    <span>Loading image...</span>
                  </div>
                ) : isCurrentImageAvailable ? (
                  <>
                    <div className='available-status' style={{ ...STYLES.imageStatus, ...STYLES.availableStatus }}>
                      Available
                    </div>
                    <ImageWithBoxes
                      src={currentImageUrl}
                      imageName={currentSection}
                      currentIndexForError={currentIndex}
                      showBoxes={!showAIProcessed}
                    />
                  </>
                ) : (
                  <div style={STYLES.errorContainer}>
                    <div style={STYLES.errorIcon}>📷</div>
                    <span>Image is Loading...</span>
                    <small>This image is taking a moment to load...</small>
                  </div>
                )}
              </div>
            </div>

            {/* AI Processed Image */}
            <div>
              <h3 style={STYLES.imageTitle}>
                {showAIProcessed ? (
                  <>
                    <CompareIcon /> AI Processed Image
                  </>
                ) : (
                  <>
                    <VisibilityIcon /> Original Image
                  </>
                )}
              </h3>
              <div className='bounding-box-second' style={STYLES.boundingbox}>
                <img
                  src={`${process.env.PUBLIC_URL}/custom-icons/Boundry-box.png`}
                  style={{
                    cursor: isCurrentImageAvailable ? 'pointer' : 'not-allowed',
                    opacity: isCurrentImageAvailable ? 1 : 0.5
                  }}
                  onClick={() => isCurrentImageAvailable && handleOpen(showAIProcessed ? 'ai' : 'original')}
                  alt='Bounding box'
                  title={isCurrentImageAvailable ? 'Expand image' : 'Image not available'}
                />
              </div>
              <div className='bounding-box-img-second' style={STYLES.imageContainer}>
                {isCurrentImageLoading ? (
                  <div style={STYLES.loadingContainer}>
                    <CircularProgress size={40} />
                    <span>Loading image...</span>
                  </div>
                ) : isCurrentImageAvailable ? (
                  <>
                    <div style={{ ...STYLES.imageStatus, ...STYLES.availableStatus }}>
                      Available
                    </div>
                    <ImageWithBoxes
                      src={currentImageUrl}
                      imageName={currentSection}
                      currentIndexForError={currentIndex}
                      showBoxes={showAIProcessed}
                    />
                  </>
                ) : (
                  <div style={STYLES.errorContainer}>
                    <div style={STYLES.errorIcon}>📷</div>
                    <span>Image is Loading...</span>
                    <small>This image is taking a moment to load...</small>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div style={STYLES.singleImageGrid}>
            <div>
              <h3 style={STYLES.imageTitle}>
                <VisibilityIcon /> {currentSection?.toUpperCase() || 'IMAGE'}
              </h3>
              <div className='bounding-box-first' style={STYLES.boundingbox}>
                <img
                  src={`${process.env.PUBLIC_URL}/custom-icons/Boundry-box.png`}
                  style={{
                    cursor: isCurrentImageAvailable ? 'pointer' : 'not-allowed',
                    opacity: isCurrentImageAvailable ? 1 : 0.5
                  }}
                  onClick={() => isCurrentImageAvailable && handleOpen('original')}
                  alt='Bounding box'
                  title={isCurrentImageAvailable ? 'Expand image' : 'Image not available'}
                />
              </div>

              <div className='bounding-box-img-first' style={STYLES.singleImageContainer}>
                {isCurrentImageLoading ? (
                  <div style={STYLES.loadingContainer}>
                    <CircularProgress size={40} />
                    <span>Loading image...</span>
                  </div>
                ) : isCurrentImageAvailable ? (
                  <>
                    <div style={{ ...STYLES.imageStatus, ...STYLES.availableStatus }}>
                      Available
                    </div>
                    <ImageWithBoxes
                      src={currentImageUrl}
                      imageName={currentSection}
                      currentIndexForError={currentIndex}
                      showBoxes={false}
                    />
                  </>
                ) : (
                  <div style={STYLES.errorContainer}>
                    <div style={STYLES.errorIcon}>📷</div>
                    <span>Image is Loading...</span>
                    <small>This image is taking a moment to load...</small>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div style={STYLES.navigation}>
        <div style={STYLES.navigationFlex}>
           <button
      onClick={handlePrev}
      style={{
        ...STYLES.prevButton,
        ...((currentIndex <= 0 || 
            (currentSection?.toLowerCase().includes('additional image') && 
             currentSection?.toLowerCase().includes('additional image 1') && 
             currentIndex === sections.findIndex(s => 
               s.toLowerCase().includes('additional image 1')
             ))) && STYLES.disabledButton)
      }}
      disabled={currentIndex <= 0 || 
               (currentSection?.toLowerCase().includes('additional image') && 
                currentSection?.toLowerCase().includes('additional image 1') && 
                currentIndex === sections.findIndex(s => 
                  s.toLowerCase().includes('additional image 1')
                ))}
    >
      <ArrowBackIcon fontSize='small' /> Prev
    </button>
            
          {!currentSection?.toLowerCase().includes('rc') && !(currentSection?.toLowerCase().includes('additional image') && currentIndex >= sections.length - 1) &&(
            <button
              onClick={handleNext}
              style={{
                ...STYLES.nextButton,
                ...(currentIndex >= sections.length - 1 && STYLES.disabledButton)
              }}
              disabled={currentIndex >= sections.length - 1}
            >
              Next <ArrowForwardIcon fontSize='small' />
            </button>
          )}
        </div>
      </div>

      {/* Modal for expanded view */}
      <Modal
        open={open}
        onClose={handleCloseModal}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          },
          '& .MuiBox-root': {
            outline: 'none',
            border: 'none'
          }
        }}
      >
        <Box sx={STYLES.modal}>
          <div className='Rec-box' style={STYLES.modalHeader}>
            <button
              onClick={handleCloseModal}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.5rem'
              }}
            >
              <CloseIcon />
            </button>
          </div>

          <div style={{
            ...STYLES.modalBody,
            display: 'flex',
            justifyContent: zoomLevel > 100 ? 'flex-start' : 'center',
            alignItems: zoomLevel > 100 ? 'flex-start' : 'center',
            overflow: zoomLevel > 100 ? 'auto' : 'hidden',
            padding: zoomLevel > 100 ? '20px' : '0'
          }}>
            {currentImageUrl && (
              <ImageWithBoxes
                src={currentImageUrl}
                imageName={currentSection}
                currentIndexForError={currentIndex}
                showBoxes={modalShowAIProcessed && showAIProcessedSection}
                zoomLevel={zoomLevel}
              />
            )}
          </div>

          <div className='percentage-btn' style={STYLES.modalFooter}>
            <div style={STYLES.zoomControl}>
              <button
                style={STYLES.zoomButton}
                onClick={handleZoomOut}
                disabled={zoomLevel <= 50}
              >
                <ZoomOutIcon fontSize='small' />
              </button>
              <span style={STYLES.zoomPercentage}>{zoomLevel}%</span>
              <button
                style={STYLES.zoomButton}
                onClick={handleZoomIn}
                disabled={zoomLevel >= 300}
              >
                <ZoomInIcon fontSize='small' />
              </button>
            </div>

            {showAIProcessedSection && (
              <button
                className='AIprocessed'
                style={STYLES.AIprocessed}
                onClick={toggleModalAIProcessed}
              >
                {modalShowAIProcessed ? (
                  <>
                    <VisibilityIcon /> Original Image
                  </>
                ) : (
                  <>
                    <CompareIcon /> AI Processed
                  </>
                )}
              </button>
            )}
          </div>
        </Box>
      </Modal>
    </div>
  )
}